<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2>{{ $t("deforestation.eudrDueDeligenceReports") }}</h2>
        <v-spacer></v-spacer>
        <v-btn v-if="isOwner" color="primary" dark class="mr-2 mb-2" @click="handleCreateReport">
          {{ $t("deforestation.createNewReport") }}
        </v-btn>
      </div>
      <v-card elevation="0" class="px-5 mt-7">
        <v-card-text>
          <v-row style="margin-left:-30px; margin-bottom: 20px; margin-top: 15px;">
            <v-col cols="12">
               <!-- Mobile card view for Due Diligence Reports -->
            <div v-if="$vuetify && $vuetify.breakpoint.smAndDown">
              <!-- Mobile Controls Bar -->
              <div class="mobile-controls-bar mb-4">
                <div class="mobile-controls-left">
                  <v-text-field 
                    prepend-inner-icon="mdi-magnify" 
                    outlined 
                    dense
                    :placeholder="$t('search')" 
                    v-model="reportSearch" 
                    @input="searchQuery" 
                    class="mobile-search"
                  />
                  <v-select 
                    v-model="reportCountry" 
                    item-text="name"
                    item-value="name" 
                    :items="countries" 
                    :label="$t('deforestation.allCountries')"
                    @change="filterData" 
                    outlined 
                    dense
                    class="mobile-filter"
                  />
                  <v-select 
                    v-model="reportStatus" 
                    item-text="name"
                    item-value="value" 
                    :items="allStatus" 
                    :label="$t('deforestation.dueDeligenceStatus')" 
                    @change="filterData"
                    outlined 
                    dense
                    class="mobile-filter"
                  />
                </div>
              </div>

              <!-- Mobile Cards -->
              <v-row>
                <v-col cols="12" v-for="item in diligenceReports" :key="item.id">
                  <v-card class="mb-3 pa-4 report-card">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="report-title">
                        <span class="font-weight-bold" style="font-size: 1.2rem;">Internal Reference #:</span>
                        <span class="font-weight-bold" style="font-size: 1.2rem;">{{ item.internalReferenceNumber }}</span>
                      </div>
                      <v-menu v-if="!isDdsExporterLockedOutForItem(item)" location="end">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item v-if="!isDdsExporter" :disabled="checkDisableReport(item)" @click="navigateReportView(item)">
                            <v-list-item-title>{{ $t('dueDiligence.viewReport') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            :disabled="!['Compliant', 'Non-Compliant', 'Processing  Farms'].includes(item.status)"
                            @click="redirectToDDSSUmmary(item.id)"
                          >
                            <v-list-item-title>{{ $t('dueDiligence.viewReportSummary') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="needsPaymentResponse(item)" @click="openPaymentResponsibilityModal(item)">
                            <v-list-item-title>{{ $t('dueDiligence.paymentResponsibilityTitle') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="canReportPayment(item)" @click="openReportPaymentModal(item)">
                            <v-list-item-title>{{ $t('dueDiligence.reportPayment') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="canReleasePaymentResponsibility(item)" @click="confirmReleasePaymentResponsibility(item)">
                            <v-list-item-title>{{ $t('dueDiligence.releasePaymentResponsibility') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="canViewInvoice(item)" @click="openViewInvoiceModal(item)">
                            <v-list-item-title>{{ invoiceMenuLabel(item) }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                    <v-divider class="mb-2"/>
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">EUDR Reference #:</span>
                      <span>{{ item.EUDRReferenceNumber  || 'N/A' }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Supplier:</span>
                      <span>{{ getSupplierFullName(item) }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Product:</span>
                      <span>{{ item.product_detail ? (item.product_detail.code ? item.product_detail.code + '-' : '') : '' }}{{ item.product_detail ? item.product_detail.name : '' }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Farms:</span>
                      <span>{{ item.productionPlaceCount }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Quantity:</span>
                      <span>{{ !isNaN(item.productNetMass) ? parseFloat(getConvertedMass(item.productNetMass)) : 0 }} {{ eudrSettings?.product_mass_unit }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Country:</span>
                      <span>{{ item.countryOfEntry }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-1 report-row">
                      <span class="font-weight-medium">Last Updated:</span>
                      <span>{{ formatDate(item.updatedAt) }}</span>
                    </div>
                    <v-divider class="my-1" />
                    <template v-if="isPtsiApprovalClient">
                      <v-divider class="my-1" />
                      <div class="d-flex align-center justify-space-between py-2 report-row">
                        <span class="font-weight-medium">{{ $t('deforestation.riskStatus') }}:</span>
                        <span v-if="isDdsExporterLockedOutForItem(item)">N/A</span>
                        <v-btn
                          v-else-if="getRiskStatusMeta(item)"
                          small
                          rounded
                          depressed
                          :class="getRiskStatusMeta(item).badgeClass"
                          height="28"
                          style="min-width:130px;"
                        >
                          {{ getRiskStatusMeta(item).label }}
                        </v-btn>
                        <span v-else>-</span>
                      </div>
                    </template>
                    <v-divider class="my-1" />
                    <div class="d-flex align-center justify-space-between py-2 report-row">
                      <span class="font-weight-medium">Due Diligence Status:</span>
                      <template v-if="isDdsExporterLockedOutForItem(item)">
                        <PaymentUnavailableStatus />
                      </template>
                      <template v-else>
                      <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                        v-if="item.status == 'Uploaded to EU Portal'">
                        <!-- getModulePermittedActions.put -->
                        {{ $t("deforestation.uploadedToEU") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="gray-button" height="34"
                        v-if="item.status == 'Pending'">
                        <!-- getModulePermittedActions.put -->
                        {{ $t("deforestation.pending") }}
                      </v-btn>
                      <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                        v-if="item.status == 'Under Review'">
                        <!-- getModulePermittedActions.put -->
                        {{ $t("deforestation.underReview") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                        v-if="isWaitingForPaymentStatus(item)">
                        {{ $t("deforestation.waitingForPayment") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                        v-if="isPaymentPendingVerificationStatus(item)">
                        {{ $t("deforestation.paymentPendingVerificationStatus") }}
                      </v-btn>

                      <v-btn small rounded depressed class="blue-button" height="34" style="width:160px; min-width:160px;"
                        v-if="isPendingApprovalStatus(item)">
                        {{ $t("deforestation.pendingApproval") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="green-button" height="34"
                        v-if="isDdsCompliantStatus(item.status) && !isPendingApprovalStatus(item) && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                        {{ $t("deforestation.compliant") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                        v-if="item.status.toLowerCase() == 'processing farms'">
                        {{ $t("deforestation.processingFarm") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="gray-button" height="34"
                        v-if="item.status.toLowerCase() == 'analyzing deforestation'">
                        {{ $t("deforestation.analyzeDeforestation") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="gray-button" height="34"
                        v-if="item.status.toLowerCase() == 'analyzing wildfire reports'">
                        {{ $t("deforestation.analyzingWildfireReports") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                        v-if="item.status.toLowerCase() == 'ready to proceed' && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                        {{ $t("deforestation.readyToProceed") }}
                      </v-btn>


                      <v-btn small rounded min-width="160" depressed class="red-button" height="34"
                        v-if="isDdsNonCompliantStatus(item.status) && !isPendingApprovalStatus(item) && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                        {{ $t("deforestation.nonCompliant") }}
                      </v-btn>

                      <v-btn small rounded min-width="160" depressed class="green-button" height="34"
                        v-if="item.status == 'Mitigation Required'">
                        {{ $t("deforestation.mitigationRequired") }}
                      </v-btn>
                      </template>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Mobile Pagination -->
              <div class="mobile-pagination mt-4">
                <div class="d-flex justify-space-between align-center">
                  <span class="mobile-pagination-label">{{ toDiligenceReports }} of {{ totalDiligenceReports }}</span>
                  <div class="mobile-pagination-controls">
                    <v-btn 
                      fab 
                      small 
                      outlined 
                      color="primary" 
                      :disabled="reportOptions.page <= 1"
                      @click="reportPageChange(false)"
                      class="mr-2"
                    >
                      <v-icon dark>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn 
                      fab 
                      small 
                      outlined 
                      color="primary" 
                      :disabled="reportOptions.page >= totalPages"
                      @click="reportPageChange(true)"
                    >
                      <v-icon dark>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>

              <v-data-table v-else :headers="reportHeaders" :items="diligenceReports" :items-per-page="10" :footer-props="{
                        'items-per-page-options': [10, 25, 50],
                      }" item-key="id" hide-default-footer :loading="loading" :options.sync="reportOptions" :loading-text="$t('loadingData')
                ">
                <template v-slot:top="{ }">
                  <div class="table-controls-bar">
                    <div class="controls-left">
                      <div class="control search">
                        <label for="text">&nbsp;</label>
                        <v-text-field prepend-inner-icon="mdi-magnify" outlined height="5px"
                          :placeholder="$t('search')" dense v-model="reportSearch" @input="searchQuery" class="shrink" />
                      </div>
                      <div class="control filter">
                        <label for="text">{{ $t("country") }}  <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon size="16" class="icon-with-background primary--text" style="color:#0EBF67" v-bind="attrs" v-on="on">
                              mdi-alert-circle
                            </v-icon>
                          </template>
                          <span>{{ $t("deforestation.EUDRcountry") }}</span>
                        </v-tooltip></label>
                        <v-select v-model="reportCountry" item-text="name"
                          item-value="name" :items="countries" :label="$t('deforestation.allCountries')"
                          @change="filterData" outlined dense />
                      </div>
                      <div class="control filter">
                        <label for="text">{{ $t('deforestation.edurDueDeligenceStatus') }}  <v-tooltip top color="00BD73" max-width="350">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon size="16" class="icon-with-background primary--text" style="color:#0EBF67" v-bind="attrs" v-on="on">
                              mdi-alert-circle
                            </v-icon>
                          </template>
                          <span>{{ $t("deforestation.EUDRDeforestationStatusTT") }}</span>
                        </v-tooltip></label>
                       
                        <v-select v-model="reportStatus" item-text="name"
                          item-value="value" :items="allStatus" :label="$t('deforestation.dueDeligenceStatus')" @change="filterData"
                          outlined dense />
                      </div>
                      </div>
                    <div class="controls-right">
                      <span class="pagination-label">{{ toDiligenceReports }} of {{ totalDiligenceReports }}</span>
                      <v-btn fab small outlined color="primary" :disabled="reportOptions.page <= 1"
                        @click="reportPageChange(false)">
                        <v-icon dark>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn fab small outlined color="primary" :disabled="reportOptions.page >= totalPages"
                        @click="reportPageChange(true)">
                        <v-icon dark>mdi-chevron-right</v-icon>
                      </v-btn>
                      <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                          <template v-slot:activator="{ on, attrs }">
                              <v-btn fab small outlined color="primary" v-bind="attrs" v-on="on">
                                  <v-icon dark>mdi-cog-outline</v-icon>
                              </v-btn>
                          </template>
                          <v-card width="417">
                              <v-list>
                                  <v-list-item>
                                      <v-list-item-content>
                                          <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                                      </v-list-item-content>
                                  </v-list-item>
                                  <v-divider></v-divider>
                                  <div class="pl-3 pt-2 font-weight-normal">
                                      {{ $t("deforestation.recordsPerpage") }}
                                  </div>
                                  <v-list-item>
                                      <v-list-item-content>
                                          <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3" step="1" ticks="always" tick-size="4"></v-slider>
                                      </v-list-item-content>
                                  </v-list-item>
                                  <v-divider class="mt-n3"></v-divider>
                              </v-list>
                              <v-card-text>
                                  <v-container fluid>
                                      <v-row>
                                          <v-col class="mt-n9" cols="6" v-for="header in reportHeaders" :key="header.value">
                                              <v-checkbox v-if="header.value" v-model="selectedHeaders" :label="header.text" color="primary" :value="header.value" hide-details></v-checkbox>
                                          </v-col>
                                      </v-row>
                                  </v-container>
                              </v-card-text>
                              <v-layout justify-center>
                                  <v-card-actions class="mb-5">
                                      <v-spacer></v-spacer>
                                      <v-btn outlined color="primary" @click="resetDefaut" width="190">
                                          {{ $t("deforestation.restoreDefault") }}
                                      </v-btn>
                                      <v-btn color="primary" @click="resetTableStructure" width="190">
                                          {{ $t("deforestation.apply") }}
                                      </v-btn>
                                  </v-card-actions>
                              </v-layout>
                          </v-card>
                      </v-menu>
                      <v-btn fab small outlined color="primary" @click="exportToPDF">
                        <v-icon dark>mdi-tray-arrow-down</v-icon>
                      </v-btn>
                      <v-btn fab small outlined color="primary" @click="printPDF">
                        <v-icon dark>mdi-printer</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
                <template v-if="diligenceReports.length" v-slot:body="{ items }">
                  <tbody>
                    <tr v-for="item in items" :key="item.id" style="position: relative; width: 0; height: 0"
                      @mouseover="selectItem(item)" @mouseleave="selectItem(false)">
                      <template v-for="h in tableHeaders">

                      <td class="text-truncate" :key="h.value + '_eudrAndInternalRefNo'" v-if="h.value == 'eudrAndInternalRefNo'">
                        {{ item.EUDRReferenceNumber + item.EUDRReferenceNumber?'-' + item.internalReferenceNumber: item.internalReferenceNumber }}
                      </td>
                     
                      <td class="text-truncate" :key="h.value + '_supplier'" v-else-if="h.value == 'supplier.firstName'">
                        {{ getSupplierFullName(item) }}
                      </td>

                      <td class="text-truncate" :key="h.value + '_product'" v-else-if="h.value == 'product'">
                        {{ item.product_detail ? (item.product_detail.code ? item.product_detail.code +'-' : "") : '' }}
                        {{ item.product_detail  ? item.product_detail.name  : '' }}
                      </td>

                      <td class="text-truncate" :key="h.value + '_productionPlaceCount'" v-else-if="h.value == 'productionPlaceCount'">
                      {{ item.productionPlaceCount }} <br />
                      {{ !isNaN(item.productNetMass) ? parseFloat(getConvertedMass(item.productNetMass)) : 0 }} {{ eudrSettings?.product_mass_unit }}
                      </td>
                     
                      <td class="text-truncate" :key="h.value + '_countryOfEntry'" v-else-if="h.value == 'countryOfEntry'">
                        {{ item.countryOfEntry }}
                      </td>

                      <td :key="h.value + '_createdAt'" v-else-if="h.value == 'createdAt'" class="text-truncate">
                        {{ getDateFormat(item.createdAt) }}
                      </td>

                      <td :key="h.value + '_riskStatus'" v-else-if="h.value == 'riskStatus'">
                        <span v-if="isDdsExporterLockedOutForItem(item)">N/A</span>
                        <v-btn
                          v-else-if="getRiskStatusMeta(item)"
                          small
                          rounded
                          min-width="150"
                          depressed
                          :class="getRiskStatusMeta(item).badgeClass"
                          height="30"
                        >
                          {{ getRiskStatusMeta(item).label }}
                        </v-btn>
                        <span v-else>-</span>
                      </td>

                      <td :key="h.value + '_status'" v-else-if="h.value == 'status'">
                        <div class="d-flex">
                          <template v-if="isDdsExporterLockedOutForItem(item)">
                            <PaymentUnavailableStatus />
                          </template>
                          <template v-else>
                          <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                            v-if="item.status == 'Uploaded to EU Portal'">
                            {{ $t("deforestation.uploadedToEU") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="gray-button" height="34"
                            v-if="item.status == 'Pending'">
                            {{ $t("deforestation.pending") }}
                          </v-btn>
                          <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                            v-if="item.status == 'Under Review'">
                            {{ $t("deforestation.underReview") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                            v-if="isWaitingForPaymentStatus(item)">
                            {{ $t("deforestation.waitingForPayment") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                            v-if="isPaymentPendingVerificationStatus(item)">
                            {{ $t("deforestation.paymentPendingVerificationStatus") }}
                          </v-btn>

                          <v-btn small rounded depressed class="blue-button" height="34" style="width:160px; min-width:160px;"
                            v-if="isPendingApprovalStatus(item)">
                            {{ $t("deforestation.pendingApproval") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="green-button" height="34"
                            v-if="isDdsCompliantStatus(item.status) && !isPendingApprovalStatus(item) && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                            {{ $t("deforestation.compliant") }}
                          </v-btn>

                          <div class="status-with-action d-flex" v-if="item.status.toLowerCase() == 'processing farms'">
                            <v-btn small rounded min-width="160" depressed class="orange-button" height="34">
                              {{ $t("deforestation.processingFarm") }}
                            </v-btn>
                            &nbsp;
                            <v-btn  v-on:click="handleRefreshDueDiligence" color="primary" fab outlined small>
                              <v-icon  dark>mdi mdi-sync</v-icon>
                            </v-btn>
                          </div>

                          <div class="d-flex" v-if="item.status.toLowerCase() == 'analyzing deforestation'">
                            <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                              >
                              {{ $t("deforestation.analyzeDeforestation") }}
                            </v-btn>
                              &nbsp;
                              <v-btn  v-on:click="handleRefreshDueDiligence" color="primary" fab outlined small>
                                <v-icon  dark>mdi mdi-sync</v-icon>
                              </v-btn>
                            </div>

                          <div class="d-flex" v-if="item.status.toLowerCase() == 'analyzing wildfire reports'">
                            <v-btn small rounded min-width="160" depressed class="gray-button" height="34"
                              >
                              {{ $t("deforestation.analyzingWildfireReports") }}
                            </v-btn>
                              &nbsp;
                              <v-btn  v-on:click="handleRefreshDueDiligence" color="primary" fab outlined small>
                                <v-icon  dark>mdi mdi-sync</v-icon>
                              </v-btn>
                            </div>

                          <v-btn small rounded min-width="160" depressed class="orange-button" height="34"
                            v-if="item.status.toLowerCase() == 'writing to blockchain'">
                            {{ $t("dueDiligence.writingToBlockchain") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="blue-button" height="34"
                              v-if="item.status.toLowerCase() == 'ready to proceed' && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                              {{ $t("deforestation.readyToProceed") }}
                            </v-btn>


                          <v-btn small rounded min-width="160" depressed class="red-button" height="34"
                            v-if="isDdsNonCompliantStatus(item.status) && !isPendingApprovalStatus(item) && !isWaitingForPaymentStatus(item) && !isPaymentPendingVerificationStatus(item)">
                            {{ $t("deforestation.nonCompliant") }}
                          </v-btn>

                          <v-btn small rounded min-width="160" depressed class="green-button" height="34"
                            v-if="item.status == 'Mitigation Required'">
                            {{ $t("deforestation.mitigationRequired") }}
                          </v-btn>
                          </template>

                          <!-- <v-spacer></v-spacer> -->
                          <v-tooltip bottom v-if="isDdsExporter && !isDdsExporterLockedOutForItem(item)">
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <img class="ml-1" :src="isIndonesianClient ? '/img/id/todo-list.svg' : isKenyaClient ? '/img/ke/todo-list.svg' : '/icons/todo-list.png'" v-if="hideOnPrint===false"
                                  style="width: 34px; height: 34px; border-radius: 50%;cursor:pointer"
                                  @click="redirectToExporterSummary(item.id)" />
                              </div>
                            </template>
                            <span>{{ $t('dueDiligence.viewReport') }}</span>
                          </v-tooltip>
                        
                          <v-tooltip v-if="!isDdsExporterLockedOutForItem(item)" bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <img class="ml-1" :src="getStatusLegendIconWithRole(item.statusLegends, isDdsExporter)"
                                  style="width: 34px; height: 34px; border-radius: 50%;cursor:pointer" />
                              </div>
                            </template>
                            <span>{{ getStatusLegendTooltipWithRole(item.statusLegends, isDdsExporter) }}</span>
                          </v-tooltip>
                          <!-- Shipment Indicator -->
                          <v-tooltip bottom v-if="!isDdsExporterLockedOutForItem(item) && isReportInShipment(item)">
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <img class="ml-1" src="/icons/dds/shipment.svg" 
                                     style="width: 34px; height: 34px; border-radius: 50%;cursor:pointer"
                                     @click="viewShipmentDetails(item)" />
                              </div>
                            </template>
                            <span>{{ $t('deforestation.shipmentInProcess') }}</span>
                          </v-tooltip>
                        </div>
                      </td>
                      <td :key="h.value + '_updatedAt'" v-else-if="h.value == 'updatedAt'" class="text-truncate">
                        {{ getDateFormat(item.updatedAt) }}
                      </td>
                      <td :key="h.value + '_action'" v-else-if="h.value == 'action'">
                        <v-menu v-if="!isDdsExporterLockedOutForItem(item)" location="start" left offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item v-if="!isDdsExporter" @click="navigateReportView(item)">
                              <v-list-item-title>{{ $t('dueDiligence.viewReport') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              :disabled="item.status === 'Pending'"
                              @click="$router.push({ name: 'EudrAssessmentReport', params: { id: item.id, type: 'assignment' } })"
                            >
                              <v-list-item-title>{{ $t('dueDiligence.share') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              :disabled="!['Compliant', 'Non-Compliant', 'Processing  Farms'].includes(item.status)"
                              @click="redirectToDDSSUmmary(item.id)"
                            >
                              <v-list-item-title>{{ $t('dueDiligence.viewReportSummary') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="needsPaymentResponse(item)" @click="openPaymentResponsibilityModal(item)">
                              <v-list-item-title>{{ $t('dueDiligence.paymentResponsibilityTitle') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canReportPayment(item)" @click="openReportPaymentModal(item)">
                              <v-list-item-title>{{ $t('dueDiligence.reportPayment') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canReleasePaymentResponsibility(item)" @click="confirmReleasePaymentResponsibility(item)">
                              <v-list-item-title>{{ $t('dueDiligence.releasePaymentResponsibility') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canViewInvoice(item)" @click="openViewInvoiceModal(item)">
                              <v-list-item-title>{{ invoiceMenuLabel(item) }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              :disabled="item.status.startsWith('Processing') || getCurrentRoles.includes('supplier') || getCurrentRoles.includes('operator')"
                              @click="deleteDiligenceReports(item.id)"
                            >
                              <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </td>
                    </template>
                    </tr>
                  </tbody>
                </template>

                <template v-slot:footer></template>
              </v-data-table>
                
            <!-- Status Legend for Shipment Creation -->
            <template>
              <StatusLegends :isDdsExporter="isDdsExporter" />
            </template>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

   
    </v-container>

    <ConfirmBox
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :cancelBtnText="confirmDelete.cancelBtnText"
      :confirmBtnText="confirmDelete.confirmBtnText"
      :confirmData="confirmDelete.confirmData"
      :dialog="confirmDeleteDialog"
      @cancel:action="confirmDeleteDialog = false"
      @continue:action="confirmDeleteDiligenceReport" />

    <ConfirmBox
      :title="releasePaymentConfirm.title"
      :message="releasePaymentConfirm.message"
      :cancelBtnText="releasePaymentConfirm.cancelBtnText"
      :confirmBtnText="releasePaymentConfirm.confirmBtnText"
      :confirmData="releasePaymentConfirm.confirmData"
      :dialog="releasePaymentConfirmDialog"
      :loading="releasePaymentConfirmLoading"
      @cancel:action="closeReleasePaymentConfirm"
      @continue:action="handleReleasePaymentResponsibility" />

    <PaymentResponsibilityModal :dialog="showPaymentResponsibilityModal" :item="selectedPaymentItem"
      @close="showPaymentResponsibilityModal = false" @respond="handlePaymentResponsibilityRespond" />

    <!-- Report Payment Modal -->
    <ReportPaymentModal :dialog="showReportPaymentModal" :item="selectedPaymentItem"
      :payment="selectedPaymentItem && selectedPaymentItem.payment"
      :bankDetails="ddsPaymentBankDetails" @close="showReportPaymentModal = false"
      @confirm="handleReportPaymentConfirm" @error="handleReportPaymentError" />

    <ViewInvoiceModal :dialog="showViewInvoiceModal" :item="selectedInvoiceItem"
      :bankDetails="ddsPaymentBankDetails" :payerInfo="selectedInvoicePayerInfo"
      @close="showViewInvoiceModal = false" />
  </div>

  
</template>
<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import DateMixin from "@/mixins/DateMixin";
import DeforestationService from "@/_services/DeforestationService";
import { getCountries } from "country-state-picker";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import ConfirmBox from "@/components/ConfirmBox";
import moment from 'moment';
//import ProfileService from "@/_services/ProfileService";
import UnitService from "@/_services/UnitService";
import RoleMixin from "@/mixins/RoleMixin";
import { mapGetters } from "vuex";
import {
  currentRoles,
  isPendingApprovalStatus,
  isDdsCompliantStatus,
  isDdsNonCompliantStatus,
  isWaitingForPaymentStatus,
  isPaymentPendingVerificationStatus,
  hasActivePaymentStatusLegend,
  isDdsExporterLockedOutByPayment,
  isIndonesianClient,
  isDdsExporter,
  isKenyaClient,
  normalizeDdsStatus,
  resolveDdsExporterPayerFields,
  getReportRiskStatusMeta,
} from "@/utils";
import tableCustomizationMixin from '@/mixins/tableCustomizationMixin';
import StatusLegends from "../../components/StatusLegends.vue";
import ReportPaymentModal from "../approval-flow/components/ReportPaymentModal.vue";
import ViewInvoiceModal from "../approval-flow/components/ViewInvoiceModal.vue";
import PaymentResponsibilityModal from "../approval-flow/components/PaymentResponsibilityModal.vue";
import PaymentUnavailableStatus from "../../components/PaymentUnavailableStatus.vue";
export default {
  components:{
    ConfirmBox,
    StatusLegends,
    ReportPaymentModal,
    ViewInvoiceModal,
    PaymentResponsibilityModal,
    PaymentUnavailableStatus,
  },
  created() {
    const prefs = this.getTablePrefs('DueDiligenceReportsTable');
    if (prefs) {
      this.selectedHeaders = prefs.selectedHeaders;
      const prefIndex = this.itemsPerPageOptions.indexOf(prefs.itemsPerPage);
      this.itemsPerPageSlider = prefIndex > -1 ? prefIndex : 1;
    } else {
      this.selectedHeaders = this.reportHeaders.map(h => h.value).filter(Boolean);
      this.itemsPerPageSlider = 1; 
    }

    this.reportOptions.itemsPerPage = this.itemsPerPageOptions[this.itemsPerPageSlider];
    if (!this.selectedHeaders.includes('riskStatus') && this.reportHeaders.some(h => h.value === 'riskStatus')) {
      const statusIdx = this.selectedHeaders.indexOf('status');
      if (statusIdx >= 0) this.selectedHeaders.splice(statusIdx, 0, 'riskStatus');
      else this.selectedHeaders.push('riskStatus');
    }
    this.tableHeaders = this.reportHeaders.filter(header =>
      this.selectedHeaders.includes(header.value) || header.value === 'action'
    );
    if (!this.selectedHeaders.includes('action') && this.reportHeaders.some(h => h.value === 'action')) {
      this.selectedHeaders.push('action');
    }

    this.getDeforestationEUDR();
  },
  async mounted() {
    localStorage.removeItem('DDS_TAB_DATA');
    await this.getEudrSettings()
  },
  watch: {
    options: {
      handler(opt) {
        this.getDeforestationEUDR(opt);
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("eudrDDS", ["getDdsUserRole"]),
    isIndonesianClient() {
      return isIndonesianClient();
    },
    isKenyaClient() {
      return isKenyaClient()
    },
    isPtsiApprovalClient() {
      return isIndonesianClient() || isKenyaClient();
    },
    isDdsExporter() {
      return isDdsExporter();
    },
    isSupplierOwner() {
        return [ "supplier_owner", "owner"].includes(
          this.getDdsUserRole
        );
    },
    isOwner() {
      return  this.getCurrentRoles.some(x => ["operator_owner","supplier_owner","dds_farms"].includes(x))
      //return ProfileService.checkRole("operator_owner") || ProfileService.checkRole("supplier_owner");
    },
    getOrganizationId() {
        return this.$store.getters.getUser?.user_organization?.id;
    },
    isOperator() {
      return  this.getCurrentRoles.includes("operator_owner")
    },
    isOperatorOnly(){
      return  this.getCurrentRoles.includes("operator")
    },
    isSupplierOnly(){
      return  this.getCurrentRoles.includes("supplier")
    },
    isSupplier() {
      return  this.getCurrentRoles.some(x => ["supplier_owner","supplier"].includes(x))
    },
    reportHeaders(){
      return this.allReportHeaders
            .map(x => {
              if (x.value === 'eudrAndInternalRefNo') {
                return {
                  ...x,
                  text: this.isKenyaClient
                    ? this.$t("deforestation.internalRefNoKenya")
                    : this.$t("deforestation.eudrAndInternalRefNo"),
                };
              }
              if (x.value === 'supplier.firstName' && this.isIndonesianClient) {
                return {
                  ...x,
                  text: this.$t("deforestation.cooperative"),
                };
              }
              return x;
            })
            .filter(header => {
              if (header.value === 'riskStatus' && !this.isPtsiApprovalClient) return false;

              // No roles defined on header → always show
              if (!header.roles || header.roles.length === 0) return true;

              // Check if the user has ANY role that exists anywhere in allReportHeaders roles
              const userIsRoleRestricted = this.allReportHeaders.some(h =>
                h.roles?.some(role => this.getCurrentRoles.includes(role))
              );

              // User's role is not in any roles array (e.g. 'dds_farm') → show all columns
              if (!userIsRoleRestricted) return true;

              // User's role IS a known role → filter by this header's roles
              return this.getCurrentRoles.some(role => header.roles.includes(role));
            });
    },
    filteredHeaders(){
      return this.tableHeaders.filter((header)=>this.showActionsColumn || header.value !== "actions")
    },
    getSupplierFullName() {
      return (item) => {
        const firstName = item.supplier ? item.supplier?.firstName : '';
        const lastName = item.supplier ? item.supplier.lastName : '';
        const fallbackName = `${firstName || ''} ${lastName || ''}`.trim();

        const reportUser = item.user;
        const cooperativeName = reportUser?.subOrg?.name || reportUser?.org?.name;
        if (cooperativeName) {
          return cooperativeName;
        }
        const userFullName = reportUser
          ? `${reportUser.firstName || ''} ${reportUser.lastName || ''}`.trim()
          : '';
        if (userFullName) {
          return userFullName;
        }

        return fallbackName;
      };
    },

  },
  data() {
    return {
      fromDiligenceReports: 0,
      toDiligenceReports: 0,
      countryModel: "",
      statusModel: "",
      regionModel: "",
      reportSearch: "",
      reportCountry: "",
      reportStatus: "",
      countries: [{ name: "All Countries" }, ...getCountries()],
      allStatus: [
         {
          name: this.$t("deforestation.uploadedToEU"),
          value: "Uploaded to EU Portal",
        },
        { name: this.$t("deforestation.pending"), value: "Pending" },
        { name: this.$t("deforestation.underReview"), value: "Under Review" },
        { name: this.$t("deforestation.nonCompliant"), value: "Non-Compliant" },
        { name: this.$t("deforestation.compliant"), value: "Compliant" },
        { 'name': this.$t("deforestation.mitigationRequired"), value: 'Mitigation Required' },
        { name: this.$t("deforestation.zeroOrNegligibleRisk"), value: "Zero/Negligible Risk" },
        { name: this.$t("deforestation.riskDetected"), value: "Risk Detected" },
      ],
      allRegions: [{ text: this.$t("deforestation.allRegions"), val: "" }],

      deforestationComplianceReports: [],
      copyReport: [],
      loading: true,
      totalDeforestationComplianceReports: 0,
      tab: 0,
      selectedItem: false,
      reportOptions: {
        itemsPerPage: 10,
        page: 1,
      },
      totalPages: 1,
      search: "",
      from: 0,
      to: 0,
      allReportHeaders: [
        {
          text: this.$t("deforestation.eudrAndInternalRefNo"),
          align: "start",
          value: "eudrAndInternalRefNo",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.supplier"),
          align: "start",
          value: "supplier.firstName",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.product"),
          align: "start",
          value: "product",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.farmsAndQuantity"),
          align: "start",
          value: "productionPlaceCount",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.country"),
          align: "start",
          value: "countryOfEntry",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.submissionDate"),
          align: "start",
          value: "createdAt",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        // {
        //   text: this.$t("deforestation.lastUpdated"),
        //   align: "start",
        //   value: "updatedAt",
        //   class: "black--text",
        //   sortable: true,
        //   roles: ["operator_owner","supplier_owner",'supplier','operator']
        // },
        {
          text: this.$t("deforestation.riskStatus"),
          align: "center",
          value: "riskStatus",
          class: "black--text",
          sortable: false,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: this.$t("deforestation.dueDeligenceStatus"),
          align: "start",
          value: "status",
          class: "black--text",
          sortable: true,
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
        {
          text: '',
          align: "center",
          value: "action",
          class: "black--text",
          sortable: false,
          width: "56px",
          roles: ["operator_owner","supplier_owner",'supplier','operator',"dds_exporter"]
        },
      ],
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "Users" },
          exact: true,
        },
        {
          text: this.$t("deforestation.eudrDueDeligenceReports"),
          disabled: true,
          href: "breadcrumbs_link_1",
        },
      ],
      diligenceReports: [],
      totalDiligenceReports: 0,
      totalDiligenceReportsPages: 0,
      showActionsColumn:true,
      selectedHeaders: [],
      tableHeaders: [],
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      menu: false,
      hideOnPrint:false,
      options: {
        page: 1,
        limit:10
      },
      confirmDeleteDialog : false,
      confirmDelete: {
        title: this.$t("confirmDialog.dueDiligenceReport.delete.title"),
        message: this.$t("confirmDialog.dueDiligenceReport.delete.message"),
        confirmBtnText: this.$t("confirmDialog.dueDiligenceReport.delete.confirmBtn"),
        cancelBtnText: this.$t("cancel"),
        confirmData: {},
      },
      eudrSettings: {},
      showPaymentResponsibilityModal: false,
      releasePaymentConfirmDialog: false,
      releasePaymentConfirmLoading: false,
      releasePaymentConfirm: {
        title: this.$t('confirmation'),
        message: this.$t('dueDiligence.releasePaymentResponsibilityConfirm'),
        confirmBtnText: this.$t('dueDiligence.releasePaymentResponsibility'),
        cancelBtnText: this.$t('cancel'),
        confirmData: {},
      },
      showReportPaymentModal: false,
      selectedPaymentItem: null,
      showViewInvoiceModal: false,
      selectedInvoiceItem: null,
      selectedInvoicePayerInfo: null,
      ddsPaymentBankDetails: {
        bankName: 'Bank Mandiri',
        accountName: 'PT Surveyor Indonesia',
        accountNumber: '123-456-7890',
      },
    };
  },
  methods: {
    isPendingApprovalStatus,
    isDdsCompliantStatus,
    isDdsNonCompliantStatus,
    isWaitingForPaymentStatus,
    isPaymentPendingVerificationStatus,
    hasActivePaymentStatusLegend,
    getRiskStatusMeta(item) {
      return getReportRiskStatusMeta(item?.riskStatus, this.$t.bind(this));
    },
    isDdsExporterLockedOutForItem(item) {
      const currentUser = this.$store.getters.getUser;
      return this.isDdsExporter && isDdsExporterLockedOutByPayment(item, currentUser?.id);
    },
    isCurrentUserDdsPayer(item) {
      // this.$store user.id is the platform-wide (cf) user id, so it must be compared
      // against payer_cf_id, not the DDS-service-internal payer_user_id.
      const currentUser = this.$store.getters.getUser;
      if (!currentUser || !item?.payment?.payer_cf_id) return false;
      return Number(item.payment.payer_cf_id) === Number(currentUser.id);
    },
    myExporterEntry(item) {
      const currentUser = this.$store.getters.getUser;
      const rows = item?.ddsReportExporters?.length
        ? item.ddsReportExporters
        : (item?.ddsReportExporter ? [item.ddsReportExporter] : []);
      if (!currentUser || !rows.length) return null;
      return rows.find(
        (exporter) => Number(exporter.exporter_cf_id) === Number(currentUser.id)
      ) || null;
    },
    needsPaymentResponse(item) {
      if (!isWaitingForPaymentStatus(item) || !item?.payment) return false;
      const myEntry = this.myExporterEntry(item);
      if (!myEntry || normalizeDdsStatus(myEntry.payment_response_status) !== 'pending') return false;
      const rows = item?.ddsReportExporters?.length
        ? item.ddsReportExporters
        : (item?.ddsReportExporter ? [item.ddsReportExporter] : []);
      const someoneElseAccepted = rows.some(
        (exporter) => normalizeDdsStatus(exporter.payment_response_status) === 'accepted'
          && Number(exporter.exporter_cf_id) !== Number(myEntry.exporter_cf_id)
      );
      return !someoneElseAccepted;
    },
    openPaymentResponsibilityModal(item) {
      this.selectedPaymentItem = item;
      this.showPaymentResponsibilityModal = true;
    },
    async handlePaymentResponsibilityRespond(payload) {
      const accept = typeof payload === 'boolean' ? payload : payload?.accept;
      const done = typeof payload === 'object' ? payload?.done : null;
      if (!this.selectedPaymentItem) {
        done?.(new Error('No payment item selected'));
        return;
      }
      try {
        await DeforestationService.respondToDdsPaymentResponsibility({
          diligenceReportId: Number(this.selectedPaymentItem.id),
          accept,
        });
        this.$toast?.success?.(
          accept
            ? this.$t('dueDiligence.paymentResponsibilityAccepted')
            : this.$t('dueDiligence.paymentResponsibilityDeclined')
        );
        this.getDeforestationEUDR();
        this.selectedPaymentItem = null;
        done?.();
      } catch (error) {
        console.error('Error responding to DDS payment responsibility:', error);
        this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
        done?.(error);
      }
    },
    canReportPayment(item) {
      if (!isWaitingForPaymentStatus(item) || !item.payment) return false;
      if (this.needsPaymentResponse(item)) return false;
      if (!['invoice_generated', 'rejected', 'pending'].includes(normalizeDdsStatus(item.payment.status))) {
        return false;
      }
      const myEntry = this.myExporterEntry(item);
      if (myEntry) {
        return normalizeDdsStatus(myEntry.payment_response_status) === 'accepted';
      }
      return this.isCurrentUserDdsPayer(item);
    },
    canReleasePaymentResponsibility(item) {
      if (!item?.payment) return false;
      if (!['pending', 'invoice_generated', 'rejected'].includes(normalizeDdsStatus(item.payment.status))) {
        return false;
      }
      const myEntry = this.myExporterEntry(item);
      return !!(myEntry && normalizeDdsStatus(myEntry.payment_response_status) === 'accepted');
    },
    confirmReleasePaymentResponsibility(item) {
      this.releasePaymentConfirm.confirmData = { item };
      this.releasePaymentConfirmLoading = false;
      this.releasePaymentConfirmDialog = true;
    },
    closeReleasePaymentConfirm() {
      if (this.releasePaymentConfirmLoading) return;
      this.releasePaymentConfirmDialog = false;
      this.releasePaymentConfirm.confirmData = {};
    },
    async handleReleasePaymentResponsibility(_action, confirmData) {
      const item = confirmData?.item || this.releasePaymentConfirm.confirmData?.item;
      if (!item) {
        this.closeReleasePaymentConfirm();
        return;
      }
      this.releasePaymentConfirmLoading = true;
      try {
        await DeforestationService.releaseDdsPaymentResponsibility({
          diligenceReportId: Number(item.id),
        });
        this.$toast?.success?.(this.$t('dueDiligence.paymentResponsibilityReleased'));
        this.getDeforestationEUDR();
      } catch (error) {
        console.error('Error releasing DDS payment responsibility:', error);
        this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
      } finally {
        this.releasePaymentConfirmLoading = false;
        this.releasePaymentConfirmDialog = false;
        this.releasePaymentConfirm.confirmData = {};
      }
    },
    openReportPaymentModal(item) {
      this.selectedPaymentItem = item;
      this.showReportPaymentModal = true;
    },
    canViewInvoice(item) {
      if (!item?.payment) return false;
      const status = normalizeDdsStatus(item.payment.status);
      if (['invoice_generated', 'reported', 'verified', 'rejected'].includes(status)) return true;
      if (status === 'pending') {
        const rows = item?.ddsReportExporters?.length
          ? item.ddsReportExporters
          : (item?.ddsReportExporter ? [item.ddsReportExporter] : []);
        const currentUser = this.$store.getters.getUser;
        const myEntry = rows.find((row) => Number(row.exporter_cf_id) === Number(currentUser?.id));
        return myEntry && normalizeDdsStatus(myEntry.payment_response_status) === 'accepted';
      }
      return false;
    },
    invoiceMenuLabel(item) {
      return item?.payment?.receipt_s3_key
        ? this.$t('dueDiligence.viewInvoiceAndReceipt')
        : this.$t('dueDiligence.viewInvoice');
    },
    openViewInvoiceModal(item) {
      this.selectedInvoiceItem = item;
      const user = this.$store.getters.getUser || {};
      const org = user.user_organization || user.organization || {};
      const payerCfId = item?.payment?.payer_cf_id;
      const rows = item?.ddsReportExporters?.length
        ? item.ddsReportExporters
        : (item?.ddsReportExporter ? [item.ddsReportExporter] : []);
      const payerExporter = rows.find(
        (exporter) => payerCfId && Number(exporter.exporter_cf_id) === Number(payerCfId)
      );
      const acceptedExporter = rows.find(
        (exporter) => normalizeDdsStatus(exporter.payment_response_status) === 'accepted'
      );
      const responsibleExporter = payerExporter || acceptedExporter;
      if (responsibleExporter) {
        this.selectedInvoicePayerInfo = resolveDdsExporterPayerFields(
          responsibleExporter,
          item?.companyID
        );
      } else {
        this.selectedInvoicePayerInfo = {
          name: [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || org.name || '-',
          companyId: item?.companyID || org.id || user.id || '-',
          contact: user.email || user.mobile || '-',
        };
      }
      this.showViewInvoiceModal = true;
    },
    async handleReportPaymentConfirm(payload) {
      if (!this.selectedPaymentItem) {
        payload?.done?.(new Error('No payment item selected'));
        return;
      }
      const { done, ...reportPayload } = payload || {};
      try {
        await DeforestationService.reportDdsPayment({
          diligenceReportId: Number(this.selectedPaymentItem.id),
          ...reportPayload,
        });
        this.$toast?.success?.(this.$t('dueDiligence.paymentReportedSuccessfully'));
        this.showReportPaymentModal = false;
        this.getDeforestationEUDR();
        done?.();
      } catch (error) {
        this.handleReportPaymentError(error);
        done?.(error);
      } finally {
        this.selectedPaymentItem = null;
      }
    },
    handleReportPaymentError(error) {
      console.error('Error reporting DDS payment:', error);
      this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
    },
    checkDisableReport(item) {
      if (!isIndonesianClient) return false;

      const { isOperatorOnly, checkDisabledStatus } = this;
      const { status, statusLegends, hasAdditionalRequest } = item;
      const isApproved = statusLegends?.toLowerCase() === 'approved';
      
      // For Indonesian clients
      if (isOperatorOnly && checkDisabledStatus(status) && isApproved) {
        return false;
      }
      
      if (!isOperatorOnly && (!checkDisabledStatus(status) || hasAdditionalRequest)) {
        return false;
      }
    },
    checkDisabledStatus(status) {
      return ['Compliant', 'Non-Compliant'].includes(status);
    },
    getStatusLegendIcon(statusLegend) {
      const iconMap = {
        'pending': '/icons/dds/pending.svg',
        'overdue': '/icons/dds/overdue.svg',
        'update_required': '/icons/dds/updaterequired.svg',
        'pending_approval': '/icons/dds/pendingApproval.svg',
        'approved': '/icons/dds/ptsiapproved.svg',
        'temporary_approved': '/icons/dds/temporaryApproved.svg',
        'rejected': '/icons/dds/rejected.svg'
      };

      return iconMap[statusLegend] || iconMap[statusLegend?.toLowerCase()] || '/icons/dds/pending.svg';
    },
    getStatusLegendTooltip(statusLegend) {
      const tooltipMap = {
        'pending': this.$t("deforestation.pendingOrNewlyReceived"),
        'overdue': this.$t("deforestation.overdue"),
        'update_required': this.$t("deforestation.updateRequired"),
        'pending_approval': this.$t("deforestation.pendingApproval"),
        'approved': this.$t("deforestation.approved"),
        'temporary_approved': this.$t("deforestation.temporaryApproved"),
        'rejected': this.$t("deforestation.rejected")
      };

      return tooltipMap[statusLegend] || tooltipMap[statusLegend?.toLowerCase()] || this.$t("deforestation.pendingOrNewlyReceived");
    },
    getStatusLegendIconWithRole(statusLegend, isDdsExporter) {
      if (statusLegend && statusLegend.toLowerCase() === 'pending' && !isDdsExporter) {
        return '/icons/dds/sent.svg';
      }

      return this.getStatusLegendIcon(statusLegend);
    },
    getStatusLegendTooltipWithRole(statusLegend, isDdsExporter) {
      if (statusLegend && statusLegend.toLowerCase() === 'pending' && !isDdsExporter) {
        return this.$t("deforestation.sentToExporter");
      }
      return this.getStatusLegendTooltip(statusLegend);
    },
    showAllDownloads() {
      this.$router.push({ name: "DownloadHistory" });
    },
    async handleRefreshDueDiligence(){
       this.getDeforestationEUDR()
    },
    async exportToPDF() {
      const requestParams = {
        page: this.reportOptions.page,
        limit: this.reportOptions.itemsPerPage,
        searchPhrase: this.reportSearch,
        country: this.reportCountry,
        status: this.reportStatus,
      };
      const querys = new URLSearchParams(requestParams).toString();
      const token = this.$store.state.auth.token
      this.startLoading()
      const url = localStorage.getItem('CF_DEFORESTATION_API_BASE_URL') || process.env.VUE_APP_DEFORESTRATION_API_BASE_URL  || "http://localhost:4043/api"
      this.downloadSourceFileWithToken(`${url}/diligence-report/diligence-report-pdf?${querys}`, "application/pdf", token,"diligence-reports.pdf")
        .then(() => {
          this.stopLoading()
        }).catch(() => {
          this.stopLoading()
      })
    },
    getConvertedMass(value){
      return this.eudrSettings && UnitService.fromBase.weight(this.eudrSettings.product_mass_unit, value)
    },
    async getEudrSettings() {
        try {
            const { data } = await DeforestationService.getEudrSettings();
            this.eudrSettings = data.eudrSetting;
        } catch (err) {
            this.$notify({
                text: this.$t('deforestation.failedToFetchSettings'),
                type: 'error'
            })
        }
    },
    async printPDF() {
      this.showActionsColumn = false;
      this.hideOnPrint =true;
      await this.$nextTick();
      const element = document.querySelector(".v-data-table__wrapper");
      let opt = {
        margin: 1,
        filename: "DiligenceReport.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };

      let worker = await html2pdf()
          .set(opt)
          .from(element)
          .toCanvas()
          .outputPdf("blob", "Suppliers.pdf");
      let file = new File([worker], "Suppliers.pdf", {
        type: "application/pdf",
      });
      var pdfUrl = await URL.createObjectURL(file);
      await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });

      this.showActionsColumn = true; // restore the column;
      this.hideOnPrint =false;
    },
    async resetDefaut() {
        this.selectedHeaders = this.reportHeaders.map(h => h.value).filter(Boolean);
        this.tableHeaders = this.reportHeaders.filter(h => this.selectedHeaders.includes(h.value));
        this.reportOptions.itemsPerPage = 10;
        this.itemsPerPageSlider = 1;
        this.menu = false;
        this.clearTablePrefs('DueDiligenceReportsTable');
        await this.getDeforestationEUDR();
    },
    async resetTableStructure() {
        this.menu = false;
        if (!this.selectedHeaders.length) {
            this.selectedHeaders = this.reportHeaders.map(h => h.value).filter(Boolean);
        }
        if (!this.selectedHeaders.includes('action') && this.reportHeaders.some(h => h.value === 'action')) {
            this.selectedHeaders.push('action');
        }
        this.tableHeaders = this.reportHeaders.filter(header =>
            this.selectedHeaders.includes(header.value) || header.value === 'action'
        );
        const selectedPerPage = this.itemsPerPageOptions[this.itemsPerPageSlider];
        this.reportOptions.itemsPerPage = selectedPerPage;
        this.setTablePrefs('DueDiligenceReportsTable', {
            selectedHeaders: this.selectedHeaders,
            itemsPerPage: selectedPerPage
        });
        await this.getDeforestationEUDR();
    },

    getDateFormat(date) {
        return moment(date).format("DD-MMM-YYYY");
    },

    redirectToDDSSUmmary(reportId){
      this.$router.push({
        name: 'viewSummaryReport',
        params:{id:reportId}
      })
    },
    redirectToExporterSummary(reportId){
      this.$router.push({
        name: 'ExporterSummaryReport',
        params:{id:reportId}
      })
    },
    handleCreateReport() {
      this.$store.dispatch('SET_DDS_TAB', {
        activeTab: 1
      }).then(() => {
        this.$router.push({ name: 'createDueDiligenceReports' })
      })
    },
    filterData() {
      this.reportOptions.page = 1;
      this.getDeforestationEUDR();
    },
    searchQuery() {
      this.reportOptions.page = 1;
      this.getDeforestationEUDR();
    },
    async getDeforestationEUDR(opt = null) {
      this.loading = true;
      const requestParams = {
        page: this.reportOptions.page,
        limit: this.reportOptions.itemsPerPage,
        searchPhrase: this.reportSearch,
        country: this.reportCountry,
        status: this.reportStatus,
        cfroles:currentRoles()
      };

      
      if(this.isOperatorOnly){
        requestParams.operatorId = this.$store.getters.getUser.id
      }

      if (opt?.sortBy?.length) {
        requestParams.orderField = opt.sortBy[0];
        requestParams.order = opt.sortDesc[0] ? "DESC" : "ASC";
      }
      if(this.isSupplierOnly){
        requestParams.supplierId = this.$store.getters.getUser.id
      }
      DeforestationService.getDiligenceReports(requestParams)
        .then((res) => {
          this.diligenceReports = res.data.diligenceReports.rows;
          this.totalDiligenceReports = res.data.diligenceReports.totalCount;
          this.totalPages = Math.ceil(this.totalDiligenceReports / this.reportOptions.itemsPerPage)

          this.fromDiligenceReports = (((this.reportOptions.page - 1) * this.reportOptions.itemsPerPage) + 1)
          this.toDiligenceReports = (this.reportOptions.page * this.reportOptions.itemsPerPage) - (this.reportOptions.itemsPerPage - res.data.diligenceReports.rows.length)

          res.data.diligenceReports.rows.forEach(row => {
            if(row.ddsReportExporter){
              row.enableShareReport = true;
            }else{
              row.enableShareReport = false;
            }

            if(row.requestAdditionalInformation && row.requestAdditionalInformation.length > 0){
              row.hasAdditionalRequest = true;
            }else{
              row.hasAdditionalRequest = false;
            }
          });
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          console.log("Error", err);
        });
    },
    async deleteDiligenceReports(id) {
      this.confirmDelete.confirmData = { id };
      this.confirmDeleteDialog = true;
    },
    async confirmDeleteDiligenceReport(action, confirmData){
      this.loading = true;

      DeforestationService.deleteDiligenceReport(parseInt(confirmData.id))
        .then(() => {
          this.getDeforestationEUDR();
          this.$notify({
            text: this.$t("deforestation.reportDeletedSuccessfully"),
            type: "success",
          });
          this.loading = false;
          this.confirmDeleteDialog = false;

        })
        .catch((err) => {
          this.loading = false;
          this.confirmDeleteDialog = false;
          this.$notify({
            text: err,
            type: "error",
          });
          console.log("Error", err);
        });
      },

    async duplicateDiligenceReports(id) {
      this.loading = true;
      DeforestationService.duplicateDiligenceReport(parseInt(id))
        .then(() => {
          this.getDeforestationEUDR();
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.$notify({
            text: err,
            type: "error",
          });
          console.log("Error", err);
        });
    },

    selectItem(item) {
      this.selectedItem = item;
    },
    resetSearchOnEmpty() {
      this.search = "";
      this.countryModel = "";
      this.statusModel = "";
      this.regionModelregionModel = "";
      this.options = {
        page: 1,
        limit: 10,
      };
      this.getDeforestationEUDR();
    },
    reportPageChange(t) {
      if (t) this.reportOptions.page = this.reportOptions.page + 1;
      else this.reportOptions.page = this.reportOptions.page - 1;
      this.getDeforestationEUDR();
    },
    navigateReportView(item) {
      let additionalInfoQuery = {};
      if (item?.requestAdditionalInformation && 
          Array.isArray(item.requestAdditionalInformation) && 
          item.requestAdditionalInformation.length > 0) {
        const latestAdditionalInfo = item.requestAdditionalInformation[item.requestAdditionalInformation.length - 1];
        if (latestAdditionalInfo?.status && 
            latestAdditionalInfo.status.toLowerCase() === 'pending') {
          additionalInfoQuery = {
            ddrAdditionalInformationId: latestAdditionalInfo.id,
          };
          this.$store.dispatch('eudrDDS/setDdsAdditionalInformationData', {
            ddrId: item.id,
            ddrAdditionalInformationId: parseInt(latestAdditionalInfo.id)
          });
        }
      }
      
      const queryParams = additionalInfoQuery.ddrAdditionalInformationId ? { query: additionalInfoQuery } : {};
      
      if (item.status == 'Pending') {
        this.$router.push({
          name: 'createDueDiligenceReports',
          params: { id: item.id },
          ...queryParams
        });
      } else if (item.status.toLowerCase() == 'under review') {
        this.$router.push({
          name: 'createDueDiligenceReports',
          params: { id: item.id, show: 'show' },
          ...queryParams
        });
      } else if (item.status.toLowerCase() == 'ready to proceed') {
        const tabd = JSON.parse(localStorage.getItem('DDS_TAB_DATA'));
        const resolvedTab = tabd ? (tabd.activeTab == 2 ? 3 : 4) : Number(item.current_step) || 3;
        // Steps past 3 (EUDR Deforestation Assessment onward) only make sense once a deforestation
        // report request has actually been made for the current set of production places. Adding
        // places clears is_dds_status_update, so without this the report reopens on step 4 showing
        // the previous run's statuses. Mirrors the guard in CustomTab.changeStep().
        const activeTb = !item.is_dds_status_update && resolvedTab > 3 ? 3 : resolvedTab;
        if (activeTb) {
          this.$store.dispatch('SET_DDS_TAB', { activeTab: activeTb }).then(() => {
            this.$router.push({
              name: 'createDueDiligenceReports',
              params: { id: item.id }, // remove show param to fix readonly features for ready to proceed reports
              ...queryParams
            });
          });
        } else {
          this.$router.push({
            name: 'createDueDiligenceReports',
            params: { id: item.id }, // remove show param to fix readonly features for ready to proceed reports
            ...queryParams
          });
        }
      } else if (item.status == 'Non-Compliant') {
        
        const finalReportTab = Number(localStorage.getItem('DDS_TAB_LENGTH')) || 6
        this.$store.dispatch('SET_DDS_TAB', { activeTab: finalReportTab }).then(() => {
          this.$router.push({
            name: 'createDueDiligenceReports',
            params: { id: item.id, show: 'non-complaint' },
            ...queryParams
          });
        });
      } else if (item.status == 'Compliant') {
        
        const finalReportTab = Number(localStorage.getItem('DDS_TAB_LENGTH')) || 6
        this.$store.dispatch('SET_DDS_TAB', { activeTab: finalReportTab }).then(() => {
          this.$router.push({
            name: 'createDueDiligenceReports',
            params: { id: item.id, show: 'complaint' },
            ...queryParams
          });
        });
      }
    },
    
    // Check if a report is already in a shipment
    isReportInShipment(item) {
      return item.shipment && item.shipment.length > 0;
    },
    
    // View shipment details for a report
    viewShipmentDetails(item) {
      if (item.shipment && item.shipment.length > 0) {
        const shipmentId = item.shipment[0].id;
        this.$router.push({ 
          name: 'shipmentDetail', 
          params: { id: shipmentId } 
        });
      }
    }

  },
  mixins: [loadingMixin, download, getPermittedActions, DateMixin, RoleMixin, tableCustomizationMixin],
};
</script>
<style lang="scss" scoped>
 ::v-deep .v-data-table__wrapper{
    margin-top: -16px !important;
  }

  .download-history{
    padding-bottom: 22px;
  }
.blue-button {
  background-color: #d0e6fe !important;
  /* Green background */
  border: 2px solid #002287 !important;
  /* Red border */
  color: #002287 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  /* Green background */
  border: 2px solid #ffa826 !important;
  /* Red border */
  color: #ffa826 !important;
}

::v-deep .wdt.v-text-field {
  width: 250px;
}

::v-deep .v-tab:hover {
  background-color: #caf1e2;
  color: $primary_color !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
}

::v-deep .center-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .img-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 40%;
}
.gray-button {
  background-color: #e8e8e8 !important;
  /* Green background */
  border: 2px solid #000000 !important;
  /* Red border */
  color: #000000 !important;
}
.no-pointer {
  pointer-events: none;
  cursor: default;
}
.table-controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 12px;
  margin-bottom: 12px;
}
.controls-left {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
}
.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-width: 0;
  padding-bottom: 22px;
}
.control {
  min-width: 120px;
  max-width: 220px;
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.pagination-label {
  font-size: 14px;
  margin-right: 0.5rem;
  white-space: nowrap;
}
@media (max-width: 1100px) {
  .table-controls-bar {
    flex-wrap: wrap;
  }
  .controls-left, .controls-right {
    flex-wrap: wrap;
    gap: 8px;
  }
  .control {
    max-width: 160px;
    flex: 1 1 120px;
  }
}
@media (max-width: 600px) {
    .v-card {
      padding: 12px !important;
      font-size: 15px;
    }
    .v-card strong {
      font-size: 15px;
    }
  }
  @media (max-width: 400px) {
    .v-card {
      font-size: 13px;
    }
  }
  .dashboard-card {
    min-height: 180px;
    padding: 32px 12px;
    width: 100%;
    max-width: 340px;
  }
  @media (max-width: 900px) {
    .dashboard-card {
      min-height: 140px;
      padding: 20px 8px;
      max-width: 100%;
    }
  }
  .report-card {
    border-radius: 18px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    font-size: 1rem;
  }
  .report-title {
    font-size: 1.2rem;
    font-weight: bold;
  }

  .report-row:last-child {
    border-bottom: none;
  }
  @media (max-width: 600px) {
    .report-card {
      padding: 12px !important;
      font-size: 15px;
    }
    .report-title {
      font-size: 1.1rem;
    }
  }

  /* Mobile Controls and Pagination Styles */
  .mobile-controls-bar {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  .mobile-controls-left {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mobile-search {
    width: 100%;
  }

  .mobile-filter {
    width: 100%;
  }

  .mobile-pagination {
    padding: 16px 0;
    border-top: 1px solid #e0e0e0;
  }

  .mobile-pagination-label {
    font-size: 14px;
    font-weight: 500;
    color: #666;
  }

  .mobile-pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Responsive adjustments for mobile */
  @media (max-width: 480px) {
    .mobile-controls-bar {
      gap: 8px;
    }
    
    .mobile-controls-left {
      gap: 8px;
    }
    
    .mobile-pagination {
      padding: 12px 0;
    }
    
    .mobile-pagination-label {
      font-size: 13px;
    }
  }
  .status-legend-inline {
  font-size: 14px;
  flex-wrap: wrap;
}
.legend-label {
  color: #222;
}
.legend-item {
  white-space: nowrap;
  font-size: 14px;
}
.status-legend-section {
  background: #f8f9fa;
  border-radius: 10px;
  flex-wrap: wrap;
  font-size: 15px;
}
.legend-title {
  color: #222;
}
.legend-item {
  white-space: nowrap;
  font-size: 15px;
}
</style>